import {
  arrayUnion,
  doc,
  serverTimestamp,
  updateDoc,
  type Firestore,
} from 'firebase/firestore'
import { recordWorkspaceActivity, type WorkspaceActivityAction } from './activityService'
import { createDeltaOperationId } from './stripboardDeltaService'
import { proofRemovalWorkflowReversion } from './checklistProofService'
import { assignAttachedFormsForItem, unassignAttachedFormsForItem } from './itemAttachmentService'
import type { ChecklistItem } from '../types/schema'
import { nextStatusAfterAssignment } from './checklistStatusMachine'
import {
  bookIdFor,
  type ChecklistCompletionState,
  type ChecklistDefinition,
} from '../types/checklistBooks'
import {
  ensurePageDetailVersionForKey,
  pageKeyFor,
  setChecklistCompletionOnPage,
} from './checklistBookService'
import {
  assignDefinition,
  updateDefinition,
} from './checklistDefinitionService'
import {
  projectLegacyChecklistTypeToNew,
} from './checklistViewAdapter'
import {
  applyDetailEditToPage,
  isChecklistDetailVersionedField,
  isChecklistRecurringScheduleField,
  pickChecklistDetailVersionPatch,
  setChecklistDetailPatchOnPage,
} from './checklistDetailVersionService'

/** Save scope for versioned-field writes on recurring items. Callers should
 *  pass 'single' or 'future' when they know the user's intent (wizard/scope
 *  dialog). Omit or pass 'global' for definition-only writes (e.g. title,
 *  schedule) that should propagate to every occurrence. */
export type ChecklistWriteScope = 'single' | 'future' | 'global'

// Legacy `checklists/{id}` writes are expected to `not-found` post-wipe; every
// other Firestore error (permission-denied, network, quota, aborted) must
// surface so the drawer's try/catch can toast a real failure instead of
// pretending the Save succeeded.
const isNotFoundError = (err: unknown): boolean =>
  (err as { code?: string })?.code === 'not-found'

const swallowLegacyNotFound = (context: string) => (err: unknown): void => {
  if (isNotFoundError(err)) {
    console.debug(`[${context}] legacy doc missing, expected post-wipe`, (err as Error)?.message)
    return
  }
  console.warn(`[${context}] failed`, (err as Error)?.message ?? err)
  throw err
}

export const CHECKLIST_CANONICAL_ACTIVITY_ACTIONS = [
  'CHECKLIST_CREATED',
  'CHECKLIST_UPDATED',
  'CHECKLIST_STATUS_CHANGED',
  'CHECKLIST_COMPLETED',
  'CHECKLIST_REOPENED',
  'CHECKLIST_DELETED',
  'CHECKLIST_DEPENDENCY_CHANGED',
  'CHECKLIST_ASSIGNED',
  'CHECKLIST_PROOF_UPDATED',
  'CHECKLIST_FORCE_COMPLETED',
  'CHECKLIST_COMPLETION_BLOCKED',
] as const

export type ChecklistCanonicalActivityAction = (typeof CHECKLIST_CANONICAL_ACTIVITY_ACTIONS)[number]

export {
  updateChecklistWorkflowStatus,
  workflowStatusToChecklistBoardStatus,
  workflowStatusIsCompleted,
  checklistStatusActivityFor,
  isChecklistHelperVisible,
  type ChecklistDeltaRunner,
  type ChecklistMutationActor,
} from './checklistStatusWriter'
import {
  workflowStatusToChecklistBoardStatus,
  type ChecklistDeltaRunner,
  type ChecklistMutationActor,
} from './checklistStatusWriter'

export const checklistDependencyMirrorFor = (
  dependencyNodeIds: readonly string[],
  itemLookup: ReadonlyMap<string, Pick<ChecklistItem, 'id' | 'title' | 'nodeKind'>>,
) => {
  const checklistDependencies = [...new Set(dependencyNodeIds)]
    .map((id) => itemLookup.get(id))
    .filter((item): item is Pick<ChecklistItem, 'id' | 'title' | 'nodeKind'> => {
      if (!item) return false
      return item.nodeKind !== 'GATE_AND' && item.nodeKind !== 'GATE_OR'
    })

  return {
    prerequisites: checklistDependencies.map((item) => item.id),
    blockedByNodeIds: checklistDependencies.map((item) => ({
      targetId: item.id,
      targetType: 'CHECKLIST' as const,
      title: item.title,
    })),
  }
}

export const checklistProofRemovalReversionFor = (
  input: {
    item: Pick<ChecklistItem, 'requiresProofFile' | 'workflowStatus' | 'previousWorkflowStatus'>
    updates: Record<string, unknown>
  },
) => {
  const isProofRemoval = input.updates.proofFileUrl === null || input.updates.proofSubmissionId === null
  const reversionStatus = isProofRemoval
    ? proofRemovalWorkflowReversion(input.item)
    : null
  return {
    reversionStatus,
    reversionPatch: reversionStatus
      ? {
          workflowStatus: reversionStatus,
          boardStatus: workflowStatusToChecklistBoardStatus(reversionStatus),
          isCompleted: false,
        }
      : {},
    defaultActivityAction: reversionStatus
      ? 'CHECKLIST_REOPENED' as ChecklistCanonicalActivityAction
      : 'CHECKLIST_UPDATED' as ChecklistCanonicalActivityAction,
  }
}

export const emitCanonicalChecklistActivity = async (
  db: Firestore,
  input: {
    workspaceId: string
    actor: ChecklistMutationActor
    targetId: string
    targetTitle: string
    action: ChecklistCanonicalActivityAction
    summary: string
    board?: string | null
    metadata?: Record<string, unknown>
  },
) => {
  await recordWorkspaceActivity(db, {
    workspaceId: input.workspaceId,
    actorId: input.actor.id,
    actorName: input.actor.name,
    targetType: 'CHECKLIST',
    targetId: input.targetId,
    targetTitle: input.targetTitle,
    action: input.action as WorkspaceActivityAction,
    summary: input.summary,
    board: input.board,
    metadata: input.metadata,
  })
}

// ---------------------------------------------------------------------------
// New-model routing (Cover / Page / LayoutVersion cutover).
// ----------------------------------------------------------------------------
// During the transition, every mutation service function dual-writes: it
// keeps its existing legacy `checklist_items` write for pre-wipe safety, and
// then routes the same intent to the new book/page/definition model via the
// helpers below. Post-wipe the legacy write silently no-ops (doc doesn't
// exist), and the new-model write is authoritative.
// ---------------------------------------------------------------------------

/** Fields whose value lives on a page's `completions[id]` in the new model. */
const COMPLETION_FIELD_KEYS = new Set<keyof ChecklistCompletionState>([
  'isCompleted', 'boardStatus', 'workflowStatus', 'previousWorkflowStatus',
  'completedAt', 'completedById', 'completedByName',
  'isReady', 'resourceReadySku', 'resourceReadyAt',
  'proofFileUrl', 'proofFileName', 'proofFileStoragePath',
  'proofSubmissionId', 'proofUploadedById', 'proofUploadedByName', 'proofUploadedAt',
  'coSignedByUid', 'coSignedByName', 'isPendingReview',
  'proofReviewStatus', 'proofReviewedAt', 'proofReviewedById', 'proofReviewedByName', 'proofRejectionNote',
  'verifiedCoordinates', 'proofVideoUrl',
  'memoStoragePath', 'memoFileName', 'memoContentType',
  'memoUploadedById', 'memoUploadedByName', 'memoUploadedAt', 'voiceMemoBlobUrl',
  'timerStartedAt', 'timerExpiredAt', 'isUrgentCrisisMode',
  'commentsCount', 'actualCost',
  'statusSyncOrigin', 'statusSyncVersion',
])

const splitUpdatesByCategory = (
  updates: Record<string, unknown>,
): { completion: Partial<ChecklistCompletionState>; definition: Partial<ChecklistDefinition> } => {
  const completion: Record<string, unknown> = {}
  const definition: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(updates)) {
    if (COMPLETION_FIELD_KEYS.has(key as keyof ChecklistCompletionState)) {
      completion[key] = value
    } else if (
      key === 'updatedAt' || key === 'lastUpdatedBy' || key === 'lastUpdatedByName' ||
      key === 'recordVersion' || key === 'position' || key === 'workspaceCanvasLayoutVersion' ||
      key === 'prerequisites' || key === 'dependencyNodeIds' ||
      key === 'blockedByNodeIds' || key === 'isBlockerForNodeIds' || key === 'gateInputIds'
    ) {
      // Audit fields are set by the service call itself; position + wire
      // fields belong to layoutVersionService, not the definition. Skip.
    } else {
      definition[key] = value
    }
  }
  return {
    completion: completion as Partial<ChecklistCompletionState>,
    definition: definition as Partial<ChecklistDefinition>,
  }
}

const routeUpdatesToBookModel = async (
  item: Pick<ChecklistItem, 'id' | 'workspaceId'> & Partial<Pick<ChecklistItem, 'checklistType' | 'previousWorkflowStatus'>>,
  updates: Record<string, unknown>,
  actor: ChecklistMutationActor,
  opts: {
    pageKey?: string | null
    scope?: ChecklistWriteScope
  } = {},
): Promise<void> => {
  const bookActor = { uid: actor.id ?? '', displayName: actor.name ?? undefined }
  if (!bookActor.uid) return
  const { completion, definition } = splitUpdatesByCategory(updates)
  const isRecurring = item.checklistType === 'DAILY' || item.checklistType === 'WEEKLY'
  const scope: ChecklistWriteScope = opts.scope ?? (isRecurring ? 'future' : 'global')

  // For recurring items, split versioned fields off the definition update and
  // route them through the detail-version model so "Only this instance" /
  // "This and future" edits do not leak into every occurrence.
  let definitionForWrite: Partial<ChecklistDefinition> = definition
  let versionedPatch: Record<string, unknown> | null = null
  if (isRecurring && scope !== 'global') {
    const partitioned: Record<string, unknown> = {}
    const rest: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(definition)) {
      if (isChecklistDetailVersionedField(key) && !isChecklistRecurringScheduleField(key)) {
        partitioned[key] = value
      } else {
        rest[key] = value
      }
    }
    if (Object.keys(partitioned).length > 0) versionedPatch = partitioned
    definitionForWrite = rest as Partial<ChecklistDefinition>
  }

  const promises: Promise<unknown>[] = []
  if (Object.keys(definitionForWrite).length > 0) {
    // Definition doc must exist for post-wipe writes; if it's genuinely
    // missing (pre-bootstrap edge), swallow that specific case but let real
    // errors propagate so the caller can surface a save failure.
    promises.push(
      updateDefinition(item.id, definitionForWrite, bookActor)
        .catch(swallowLegacyNotFound('checklistMutationService.definition-update')),
    )
  }

  if (isRecurring && versionedPatch) {
    const type = projectLegacyChecklistTypeToNew(item.checklistType)
    const bookId = bookIdFor(item.workspaceId, type)
    let pageKey = opts.pageKey
    if (!pageKey) {
      const { fetchWorkspaceRawTimezone } = await import('./checklistStatusWriter')
      const { workspaceTodayKey } = await import('./repeat/keys')
      const rawTz = await fetchWorkspaceRawTimezone(item.workspaceId)
      const cadence: 'DAILY' | 'WEEKLY' = type === 'WEEKLY' ? 'WEEKLY' : 'DAILY'
      pageKey = workspaceTodayKey(cadence, rawTz)
    }
    const patch = pickChecklistDetailVersionPatch(versionedPatch)
    if (Object.keys(patch).length > 0) {
      promises.push(
        (async () => {
          await ensurePageDetailVersionForKey(bookId, item.workspaceId, type, pageKey, bookActor)
          if (scope === 'single') {
            await setChecklistDetailPatchOnPage(bookId, pageKey, item.id, patch, bookActor)
          } else {
            await applyDetailEditToPage(bookId, item.workspaceId, pageKey, item.id, patch, bookActor)
          }
        })(),
      )
    }
  }

  if (Object.keys(completion).length > 0) {
    const type = projectLegacyChecklistTypeToNew(item.checklistType)
    const isDailyOrWeekly = type === 'DAILY' || type === 'WEEKLY'
    let pageKey = opts.pageKey
    if (!pageKey && isDailyOrWeekly) {
      const { fetchWorkspaceRawTimezone } = await import('./checklistStatusWriter')
      const { workspaceTodayKey } = await import('./repeat/keys')
      const rawTz = await fetchWorkspaceRawTimezone(item.workspaceId)
      const cadence: 'DAILY' | 'WEEKLY' = type === 'WEEKLY' ? 'WEEKLY' : 'DAILY'
      pageKey = workspaceTodayKey(cadence, rawTz)
    } else if (!pageKey) {
      const { fetchWorkspaceRawTimezone } = await import('./checklistStatusWriter')
      const rawTz = await fetchWorkspaceRawTimezone(item.workspaceId)
      pageKey = pageKeyFor(type, new Date(), rawTz)
    }
    const bookId = bookIdFor(item.workspaceId, type)
    promises.push(
      setChecklistCompletionOnPage(bookId, pageKey, item.id, completion as ChecklistCompletionState, bookActor),
    )
  }
  await Promise.all(promises)
}

export const updateChecklistFields = async (
  db: Firestore,
  input: {
    item: Pick<ChecklistItem, 'id' | 'workspaceId' | 'title' | 'board'>
      & Partial<Pick<ChecklistItem, 'requiresProofFile' | 'workflowStatus' | 'previousWorkflowStatus' | 'checklistType'>>
    updates: Record<string, unknown>
    actor: ChecklistMutationActor
    pageKey?: string | null
    /** For DAILY/WEEKLY items: 'single' writes versioned fields to the
     *  focused page's `detailPatch`; 'future' mints a new detail-version
     *  branch from that page forward; 'global' writes directly to the
     *  definition (only appropriate for title, schedule, etc.). When
     *  omitted, recurring items default to 'future' — safer than silently
     *  clobbering every past/present/future instance. */
    scope?: ChecklistWriteScope
    action?: ChecklistCanonicalActivityAction
    summary?: string
    metadata?: Record<string, unknown>
    /** Set to false when the caller (e.g. ChecklistDrawingBoard's board Save
     *  path) is already emitting its own dependency-change activity events
     *  per-diff and would otherwise cause a duplicate CHECKLIST_UPDATED /
     *  CHECKLIST_DEPENDENCY_CHANGED per item. Defaults to true — every
     *  pre-existing caller keeps its emission. */
    emitActivity?: boolean
  },
) => {
  const { reversionStatus, reversionPatch, defaultActivityAction } = checklistProofRemovalReversionFor({
    item: input.item,
    updates: input.updates,
  })
  const isRecurring = input.item.checklistType === 'DAILY' || input.item.checklistType === 'WEEKLY'
  const scope: ChecklistWriteScope = input.scope ?? (isRecurring ? 'future' : 'global')

  // Legacy checklists doc still exists for pre-wipe workspaces. Skip
  // versioned fields on recurring writes so the legacy doc isn't the
  // source of truth for per-page overrides — the book model owns those.
  const legacyPayload: Record<string, unknown> = {}
  for (const [key, value] of Object.entries({ ...input.updates, ...reversionPatch })) {
    if (isRecurring && scope !== 'global' && isChecklistDetailVersionedField(key) && !isChecklistRecurringScheduleField(key)) continue
    legacyPayload[key] = value
  }
  if (Object.keys(legacyPayload).length > 0) {
    // Post-wipe there is no legacy doc — swallow "not-found" and let the
    // new-model route below be authoritative. Any other Firestore error
    // (permission-denied, network) now propagates so the caller can toast.
    await updateDoc(doc(db, 'checklists', input.item.id), {
      ...legacyPayload,
      updatedAt: serverTimestamp(),
      lastUpdatedBy: input.actor.id ?? null,
      lastUpdatedByName: input.actor.name ?? null,
    }).catch(swallowLegacyNotFound('checklistMutationService.legacy-field-update'))
  }
  await routeUpdatesToBookModel(
    input.item,
    { ...input.updates, ...reversionPatch },
    input.actor,
    { pageKey: input.pageKey ?? null, scope },
  )
  if (input.emitActivity !== false) {
    await emitCanonicalChecklistActivity(db, {
      workspaceId: input.item.workspaceId,
      actor: input.actor,
      targetId: input.item.id,
      targetTitle: input.item.title,
      action: input.action ?? defaultActivityAction,
      summary: input.summary ?? `Updated checklist item ${input.item.title}`,
      board: input.item.board ?? 'DASHBOARD',
      metadata: {
        ...input.metadata,
        ...(reversionStatus ? { proofRemovalRevertedTo: reversionStatus } : {}),
      },
    })
  }
}

export const assignChecklistItem = async (
  db: Firestore,
  input: {
    item: Pick<ChecklistItem, 'id' | 'workspaceId' | 'title' | 'board' | 'assigneeId' | 'attachedFormIds'>
      & Partial<Pick<ChecklistItem, 'workflowStatus' | 'isCompleted' | 'checklistType' | 'assigneeHistoryIds'>>
    assignee: { userId: string; displayName: string } | null
    actor: Required<ChecklistMutationActor>
    /** Recurring-only: the focused occurrence page to write to when
     *  `scope` is 'single' or 'future'. Ignored for INDIVIDUAL. */
    pageKey?: string | null
    /** Save scope for recurring items:
     *   - 'single' → per-occurrence page.detailPatch override; other
     *      occurrences continue to inherit the definition's assignee.
     *   - 'future' → mints a new detailVersion from `pageKey` onward.
     *   - 'global' → writes to the checklist definition (affects every
     *      past and future occurrence).
     *  Non-recurring items ignore this and always write to the
     *  definition. Omitted → 'global' (backwards compatible). */
    scope?: ChecklistWriteScope
  },
) => {
  const isRecurring = input.item.checklistType === 'DAILY' || input.item.checklistType === 'WEEKLY'
  const scope: ChecklistWriteScope = isRecurring
    ? (input.scope ?? 'global')
    : 'global'
  const nextStatus = nextStatusAfterAssignment(
    input.item.workflowStatus,
    Boolean(input.assignee?.userId),
  )
  const statusPayload = nextStatus !== null
    ? {
        workflowStatus: nextStatus,
        boardStatus: workflowStatusToChecklistBoardStatus(nextStatus),
        previousWorkflowStatus: input.item.workflowStatus ?? null,
        isCompleted: false,
      }
    : null

  const assigneePatch: Partial<ChecklistDefinition> = {
    assigneeId: input.assignee?.userId ?? null,
    assigneeName: input.assignee?.displayName ?? null,
    ...(input.assignee?.userId
      ? {
          assigneeHistoryIds: [
            ...(input.item.assigneeHistoryIds ?? []),
            input.assignee.userId,
          ],
        }
      : {}),
  }

  if (scope === 'global') {
    // Write to book-model page completions first if status was promoted, so projection sees page update
    if (statusPayload) {
      await routeUpdatesToBookModel(
        input.item,
        statusPayload,
        input.actor,
        { pageKey: input.pageKey ?? null, scope: 'global' },
      )
    }

    // Book-model definition is the authoritative post-wipe write. Failures
    // here MUST propagate so the caller sees an assignment that didn't
    // actually persist — the earlier console.debug hid this from users.
    await assignDefinition(
      input.item.id,
      input.assignee ? { userId: input.assignee.userId, displayName: input.assignee.displayName } : null,
      { uid: input.actor.id || '', displayName: input.actor.name },
      { skipStatusPromotion: true },
    )

    // Legacy doc write happens last so projection listeners see the page update first
    await updateDoc(doc(db, 'checklists', input.item.id), {
      assigneeId: assigneePatch.assigneeId ?? null,
      assigneeName: assigneePatch.assigneeName ?? null,
      ...(input.assignee?.userId ? { assigneeHistoryIds: arrayUnion(input.assignee.userId) } : {}),
      ...(statusPayload ?? {}),
      updatedAt: serverTimestamp(),
      lastUpdatedBy: input.actor.id,
      lastUpdatedByName: input.actor.name,
    }).catch(swallowLegacyNotFound('checklistMutationService.legacy-assign'))
  } else {
    // Recurring 'single' / 'future' — assigneeId/Name/HistoryIds are
    // versioned fields; route through the same detail-version pipeline
    // that updateChecklistFields uses so the override lives on the page's
    // detailPatch ('single') or a fresh detailVersion ('future').
    if (!input.pageKey) {
      throw new Error(`assignChecklistItem: scope=${scope} requires an explicit pageKey`)
    }
    await routeUpdatesToBookModel(
      input.item,
      {
        ...(assigneePatch as Record<string, unknown>),
        ...(statusPayload ?? {}),
      },
      input.actor,
      { pageKey: input.pageKey, scope },
    )
  }

  await emitCanonicalChecklistActivity(db, {
    workspaceId: input.item.workspaceId,
    actor: input.actor,
    targetId: input.item.id,
    targetTitle: input.item.title,
    action: 'CHECKLIST_ASSIGNED',
    summary: input.assignee
      ? scope === 'single'
        ? `Assigned to ${input.assignee.displayName} for this occurrence`
        : scope === 'future'
          ? `Assigned to ${input.assignee.displayName} for this and future occurrences`
          : `Assigned to ${input.assignee.displayName}`
      : scope === 'single'
        ? 'Removed the assignee for this occurrence'
        : scope === 'future'
          ? 'Removed the assignee for this and future occurrences'
          : 'Removed the assignee',
    board: input.item.board ?? 'DASHBOARD',
    metadata: {
      previousAssigneeId: input.item.assigneeId ?? null,
      assigneeId: input.assignee?.userId ?? null,
      scope,
      ...(input.pageKey ? { pageKey: input.pageKey } : {}),
    },
  })

  // Form assignment side-effect: definition-scoped (forms attach to the
  // canonical checklist regardless of occurrence). Skip on 'single' scope
  // so a one-off reassignment doesn't rewire form ownership for every
  // occurrence — that would defeat the "just this instance" intent.
  if (scope !== 'single') {
    const attachmentTarget = {
      id: input.item.id,
      workspaceId: input.item.workspaceId,
      title: input.item.title,
      kind: 'CHECKLIST' as const,
      collectionName: 'checklists' as const,
      attachedFormIds: input.item.attachedFormIds ?? [],
    }
    if (input.item.assigneeId && input.item.assigneeId !== input.assignee?.userId) {
      await unassignAttachedFormsForItem(attachmentTarget, [input.item.assigneeId])
    }
    if (input.assignee?.userId) {
      await assignAttachedFormsForItem(attachmentTarget, [input.assignee.userId])
    }
  }
}


export const updateChecklistDependencies = async (
  input: {
    item: Pick<ChecklistItem, 'id' | 'boardStatus' | 'recordVersion'>
      & Partial<Pick<ChecklistItem, 'workspaceId'>>
    dependencyNodeIds: string[]
    runDelta: ChecklistDeltaRunner
    actor?: ChecklistMutationActor
  },
) => {
  const result = await input.runDelta({
    operationId: createDeltaOperationId('checklist-dependencies', input.item.id),
    operationType: 'DEPENDENCY_UPDATE',
    recordId: input.item.id,
    recordKind: 'CHECKLIST',
    sourceStatus: input.item.boardStatus,
    destinationStatus: input.item.boardStatus,
    expectedVersion: input.item.recordVersion,
    dependencyNodeIds: [...new Set(input.dependencyNodeIds)],
  })
  if (input.actor && input.item.workspaceId) {
    // Book-model definition update — propagate real errors so a dependency
    // graph change the user thinks succeeded doesn't silently vanish.
    await updateDefinition(
      input.item.id,
      { dependencyNodeIds: [...new Set(input.dependencyNodeIds)] },
      { uid: input.actor.id ?? '', displayName: input.actor.name ?? undefined },
    ).catch(swallowLegacyNotFound('checklistMutationService.deps-update'))
  }
  return result
}
